// src/services/circles/FriendCirclesService.js
//
// Every Friend Circles read and write the app makes. firestore.rules pins each
// write to the exact shape sent from here, and the rules suite drives the
// allowed cases through these methods, so a change in shape here fails the
// suite rather than failing quietly in someone's circle.
//
// Since October 2026 a circle holds no writing (docs/CIRCLES_PULSE.md): what
// the circle is on, a fixed-option question everyone answers before anyone
// sees an answer, and prompts to talk about in person. The written group
// reflections, Question of the Day, comments, reactions, member removal,
// nudges and the "every member" challenges are gone, along with the code that
// read and wrote them.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { answerCompletesPulse, newPulseFor } from '../../utils/circlePulse';

const REGION = 'australia-southeast1';

const fail = (error, fallback) => ({
  success: false,
  error: error?.message || fallback,
  code: error?.code || null,
});

async function callable(name, payload) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { app } = await import('../config/firebase');
  const fn = httpsCallable(getFunctions(app, REGION), name);
  const { data } = await fn(payload);
  return data;
}

class FriendCirclesService {
  /**
   * Create a new friend circle, holding only its creator.
   *
   * No owner, no queue, no cap - deliberately. The modules teach that groups
   * bond by defining an outside, that exclusion registers much like physical
   * pain, and that sitting on the edge of a group is quietly awful. A circle
   * with a host who approves arrivals and can remove people is that shape
   * exactly. `createdBy` is kept as a record of who started the circle; it
   * confers no power over anyone else.
   *
   * The createCircle callable writes it, not this browser: the server gives
   * it an invite code no other circle holds and records that code, so the
   * code can only ever lead here. (When the browser wrote circles itself,
   * anyone who had seen a circle's code could start a copy holding the same
   * one and catch the people it was sent to.) The circle is then read back,
   * as its only member, so the app holds exactly what was stored.
   */
  async createCircle(userId, circleName, emoji = '🌟', username = null) {
    try {
      const name = String(circleName || '').trim().slice(0, 30);
      const { circleId, inviteCode } = await callable('createCircle', {
        name,
        emoji,
        username: username ? String(username).slice(0, 60) : null,
      });

      // If the read-back fails the circle still exists, so carry on with
      // what was sent rather than report a failure.
      const stored = await this.getCircle(circleId);
      const circle = stored.success ? stored.circle : {
        id: circleId,
        name,
        emoji,
        createdBy: userId,
        members: [userId],
        memberStatus: { [userId]: { status: 'active', username: username || null } },
        inviteCode,
        settings: { isPrivate: true, allowNewMembers: true, currentModule: null },
      };

      return { success: true, circleId, inviteCode, circle };
    } catch (error) {
      console.error('Error creating circle:', error);
      const code = error?.code || '';
      if (code.includes('invalid-argument') || code.includes('resource-exhausted')) {
        return { success: false, error: error.message, code };
      }
      if (code.includes('unauthenticated')) {
        return { success: false, error: 'Sign in first, then try again', code };
      }
      return { success: false, error: 'Could not start the circle right now. Try again in a moment.', code };
    }
  }

  /**
   * Join a circle with its invite code.
   *
   * The lookup happens in the joinCircleByCode callable, under the Admin SDK,
   * so the client never needs to read a circle it isn't in. Having the code IS
   * the invitation: there is no approval step, so this either joins or fails.
   */
  async joinCircle(userId, inviteCode, username = null) {
    try {
      const data = await callable('joinCircleByCode', { inviteCode, username });
      return {
        success: true,
        alreadyMember: data?.status === 'already-member',
        circleId: data?.circleId || null,
        circleName: data?.name || null,
      };
    } catch (error) {
      console.error('Error joining circle:', error);
      const code = error?.code || '';
      if (code.includes('not-found') || code.includes('invalid-argument')) {
        return { success: false, error: error.message };
      }
      if (code.includes('unauthenticated')) {
        return { success: false, error: 'Sign in first, then try the code again' };
      }
      return { success: false, error: 'Could not join right now. Try again in a moment.' };
    }
  }

  /**
   * Swap the circle's invite code for a new one. Anyone in the circle may: if
   * the code has ended up somewhere it shouldn't, whoever notices first can
   * kill it. The old code stops working at once; nobody already in is
   * affected.
   */
  async rotateInviteCode(circleId) {
    try {
      const data = await callable('rotateCircleCode', { circleId });
      if (!data?.inviteCode) throw new Error('No new code came back');
      return { success: true, inviteCode: data.inviteCode };
    } catch (error) {
      console.error('Error getting a new code:', error);
      return { success: false, error: 'Could not get a new code right now. Try again in a moment.' };
    }
  }

  /**
   * Record that something happened in this circle, for isDormant() below.
   * Only the circle's own stamp: when each person was last around is
   * presence, and circles don't show presence.
   *
   * Best-effort and never surfaced. The worst case of a failed stamp is that
   * the quiet-circle notice appears a little early, and that notice only ever
   * offers, never acts.
   */
  async touchActivity(circleId) {
    if (!circleId) return { success: false };
    try {
      await updateDoc(doc(db, 'circles', circleId), { lastActivityAt: serverTimestamp() });
      return { success: true };
    } catch (error) {
      return fail(error, 'Could not stamp the circle');
    }
  }

  /**
   * Has this circle gone quiet?
   *
   * A circle formed while a group was happy outlives the group being happy.
   * Rather than leave it sitting there as a monument, the dashboard uses this
   * to offer leaving - gently, and only to people already inside it.
   */
  isDormant(circle, days = 60) {
    const stamp = circle?.lastActivityAt?.toDate?.() || circle?.lastActivityAt;
    if (!stamp) return false;
    const last = stamp instanceof Date ? stamp : new Date(stamp);
    if (Number.isNaN(last.getTime())) return false;
    return (Date.now() - last.getTime()) / 86400000 > days;
  }

  /**
   * Deliberately absent: approveMember, denyMember, removeMember, nudgeMember,
   * and anything that writes words into a circle.
   *
   * Nobody approves anyone (the code is the invitation), nobody can remove
   * anyone but themselves (see leaveCircle), nudging is gone, and a circle has
   * nowhere to type.
   */

  /** All the circles this person is in. */
  async getUserCircles(userId) {
    try {
      const q = query(collection(db, 'circles'), where('members', 'array-contains', userId));
      const snapshot = await getDocs(q);
      // Offline, getDocs falls back to the (possibly empty) local cache. An
      // empty answer from the cache isn't "you're in no circles" - treat it as
      // not knowing, so nobody is bounced out of their circle by a bad signal.
      if (snapshot.empty && snapshot.metadata?.fromCache) {
        return { success: false, error: 'offline', circles: [] };
      }
      const circles = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      return { success: true, circles };
    } catch (error) {
      console.error('Error fetching circles:', error);
      return { success: false, error: error.message, circles: [] };
    }
  }

  async getCircle(circleId) {
    try {
      const snap = await getDoc(doc(db, 'circles', circleId));
      if (!snap.exists()) return { success: false, error: 'Circle not found' };
      return { success: true, circle: { id: snap.id, ...snap.data() } };
    } catch (error) {
      console.error('Error fetching circle:', error);
      return fail(error, 'Could not load the circle');
    }
  }

  /**
   * Leave a circle. Works the same for everyone, including whoever started
   * it: nobody inherits anything, because there is nothing to inherit.
   *
   * You take your name (memberStatus entry) and your answer with you, and come
   * off the current question. If you are the last one in, leaving deletes the
   * circle, so an empty circle never sits there holding a live invite code.
   * Nobody else is told.
   */
  async leaveCircle(userId, circleId) {
    try {
      const circleRef = doc(db, 'circles', circleId);
      const snap = await getDoc(circleRef);
      if (!snap.exists()) return { success: false, error: 'Circle not found' };
      const circle = snap.data();

      const answerRef = doc(db, 'circles', circleId, 'pulse_answers', userId);
      let hasAnswer = false;
      try {
        hasAnswer = (await getDoc(answerRef)).exists();
      } catch {
        // Unreadable means there is nothing of yours there to delete.
      }

      const batch = writeBatch(db);
      if (hasAnswer) batch.delete(answerRef);

      const others = (circle.members || []).filter((id) => id !== userId);
      if (others.length === 0) {
        batch.delete(circleRef);
      } else {
        const update = {
          members: arrayRemove(userId),
          [`memberStatus.${userId}`]: deleteField(),
          lastActivityAt: serverTimestamp(),
        };
        if (circle.pulse) {
          update['pulse.askedTo'] = arrayRemove(userId);
          update['pulse.answeredBy'] = arrayRemove(userId);
        }
        batch.update(circleRef, update);
      }
      await batch.commit();

      return { success: true, closed: others.length === 0 };
    } catch (error) {
      console.error('Error leaving circle:', error);
      return fail(error, 'Could not leave the circle');
    }
  }

  /** Pick the module the circle is on. Any member may; nobody owns the circle. */
  async setCurrentModule(circleId, moduleId) {
    try {
      await updateDoc(doc(db, 'circles', circleId), {
        'settings.currentModule': Number(moduleId),
        lastActivityAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error setting the circle module:', error);
      return fail(error, 'Could not change the module');
    }
  }

  /**
   * Tick yourself off as having finished a module, in this circle. Only
   * whether - not how far through, how long it took, or anything written in
   * it. Only recorded for the module the circle is on.
   */
  async recordModuleRead(circleId, userId, moduleId) {
    try {
      await updateDoc(doc(db, 'circles', circleId), {
        [`memberStatus.${userId}.modulesRead`]: arrayUnion(Number(moduleId)),
      });
      return { success: true };
    } catch (error) {
      return fail(error, 'Could not record the module');
    }
  }

  /** Ask the circle a new question: the next one round, to everyone in it now. */
  async startPulse(circle) {
    try {
      const pulse = newPulseFor(circle);
      await updateDoc(doc(db, 'circles', circle.id), {
        pulse: { ...pulse, startedAt: serverTimestamp() },
        lastActivityAt: serverTimestamp(),
      });
      return { success: true, pulse };
    } catch (error) {
      console.error('Error starting the question:', error);
      return fail(error, 'Could not ask the question');
    }
  }

  /**
   * Lock in your answer to the circle's current question: one write for your
   * answer document and one adding you to answeredBy, in a single batch so
   * one can't happen without the other. If you are the last one asked, the
   * same batch reveals.
   */
  async answerPulse(circle, userId, choice) {
    try {
      const pulse = circle?.pulse;
      if (!pulse?.id) return { success: false, error: 'There is no question to answer' };

      const batch = writeBatch(db);
      batch.set(doc(db, 'circles', circle.id, 'pulse_answers', userId), {
        pulseId: pulse.id,
        choice: Number(choice),
        answeredAt: serverTimestamp(),
      });
      const update = {
        'pulse.answeredBy': arrayUnion(userId),
        lastActivityAt: serverTimestamp(),
      };
      const reveals = answerCompletesPulse(pulse, userId);
      if (reveals) {
        update['pulse.revealed'] = true;
        update['pulse.revealedAt'] = serverTimestamp();
      }
      batch.update(doc(db, 'circles', circle.id), update);
      await batch.commit();

      return { success: true, revealed: reveals };
    } catch (error) {
      console.error('Error saving the answer:', error);
      return fail(error, 'Could not save your answer');
    }
  }

  /**
   * Show the answers: once everyone asked has answered, or, with at least two
   * in, once the question is three days old. The rules check which.
   */
  async revealPulse(circleId) {
    try {
      await updateDoc(doc(db, 'circles', circleId), {
        'pulse.revealed': true,
        'pulse.revealedAt': serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error revealing the answers:', error);
      return fail(error, 'Could not show the answers');
    }
  }

  /** Your own answer to the circle's current question, or null. */
  async getMyPulseAnswer(circle, userId) {
    try {
      const snap = await getDoc(doc(db, 'circles', circle.id, 'pulse_answers', userId));
      if (!snap.exists() || snap.data().pulseId !== circle?.pulse?.id) {
        return { success: true, choice: null };
      }
      return { success: true, choice: snap.data().choice };
    } catch (error) {
      return { ...fail(error, 'Could not load your answer'), choice: null };
    }
  }

  /**
   * Everyone's answers to a revealed question, as { uid: choice }. Read one
   * document per person who answered: the rules let you read another
   * member's answer only once the question is revealed, and only if you were
   * asked it.
   */
  async getPulseAnswers(circle) {
    try {
      const pulse = circle?.pulse;
      if (!pulse?.revealed) return { success: true, answers: {} };
      const results = await Promise.all((pulse.answeredBy || []).map(async (uid) => {
        const snap = await getDoc(doc(db, 'circles', circle.id, 'pulse_answers', uid));
        return snap.exists() && snap.data().pulseId === pulse.id ? [uid, snap.data().choice] : null;
      }));
      return { success: true, answers: Object.fromEntries(results.filter(Boolean)) };
    } catch (error) {
      console.error('Error loading the answers:', error);
      return { ...fail(error, 'Could not load the answers'), answers: {} };
    }
  }
}

export const friendCirclesService = new FriendCirclesService();
export default friendCirclesService;
